/**
 * The till's grid, read from this device's own copy of the catalogue.
 *
 * What the Sales screen shows when the server cannot be reached. Until this
 * existed the offline grid was whatever the service worker happened to have
 * cached: only the exact searches and categories already opened with signal,
 * and nothing at all once those entries were a day old. A till that has been
 * offline since yesterday must still have every product on it.
 *
 * The copy is kept fresh by `pull()` in the sync engine, which the sync store
 * runs on start, on reconnect and every quarter of an hour. Stock is
 * `serverQty + pendingDelta`, so a sale rung up offline is already off the
 * shelf figure here — which the service worker's cached response never was.
 */

import { db } from "@/db/database";
import type { LocalProduct } from "@/db/schema";

import { localStockRepository } from "./localStockRepository";

import type { TillProduct } from "./salesRepository";

/** The same cap the server puts on its grid response. */
const GRID_LIMIT = 200;

export const localCatalogueRepository = {
  async gridProducts(
    params: { q?: string | undefined; category?: string | undefined } = {},
  ): Promise<TillProduct[]> {
    const term = (params.q ?? "").trim().toLowerCase();

    const products = (await db.products.toArray())
      .filter((product) => product.isActive)
      .filter((product) => !params.category || product.categoryId === params.category)
      .filter((product) => !term || matches(product, term))
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, GRID_LIMIT);

    // The counter the cashier chose, or every counter this till knows about —
    // the same as the server's grid does online.
    const session = await db.auth.get("current");
    const stock = await localStockRepository.onHand(
      products.map((product) => product.id),
      session?.activeSectionId ?? null,
    );

    return products.map((product) => {
      const onHand = stock.get(product.id) ?? 0;
      return {
        id: product.id,
        name: product.name,
        selling_price: product.sellingPrice,
        tax_rate: product.taxRate,
        category: product.categoryId,
        image_url: product.imageUrl,
        image_thumb_url: product.imageThumbUrl ?? product.imageUrl,
        stock_on_hand: onHand.toFixed(3),
        // Needs the recipe and every ingredient's balance; the local copy has
        // neither. Unknown, rather than a number that might be wrong.
        makeable: null,
      };
    });
  },
};

/**
 * Name, nickname, SKU or barcode — the fields the server's search covers, so
 * "coke" finds Coca-Cola whether or not there is signal.
 */
function matches(product: LocalProduct, term: string): boolean {
  return (
    product.name.toLowerCase().includes(term) ||
    product.searchKeywords.toLowerCase().includes(term) ||
    (product.sku ?? "").toLowerCase().includes(term) ||
    product.barcodes.some((barcode) => barcode === term)
  );
}
